import React from "react";
import { Box, Container, Typography, Paper, Divider } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ArrowBack, Security, Policy, Info } from "@mui/icons-material";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const sections = [
    {
      title: "1. Information We Collect",
      content: `We collect information that you provide directly to us, including:
      
• Personal Information: Name, email address, phone number, and other contact details
• Account Information: Username, password, and preferences
• Professional Information: Job title, department, role, and organizational details
• Usage Data: Information about how you use our platform, including tasks, activities, and interactions
• Device Information: IP address, browser type, operating system, and device identifiers`,
    },
    {
      title: "2. How We Use Your Information",
      content: `We use the information we collect to:
      
• Provide, maintain, and improve our services
• Process and complete transactions
• Send administrative information, updates, and security alerts
• Respond to your comments, questions, and customer service requests
• Monitor and analyze trends, usage, and activities
• Detect, prevent, and address technical issues and security threats
• Personalize your experience and deliver relevant content
• Comply with legal obligations and enforce our terms`,
    },
    {
      title: "3. Information Sharing and Disclosure",
      content: `We may share your information in the following circumstances:
      
• With Your Consent: We share information when you give us explicit permission
• Service Providers: With third-party vendors who perform services on our behalf
• Business Transfers: In connection with mergers, acquisitions, or asset sales
• Legal Requirements: When required by law or to protect our rights and safety
• Within Your Organization: With other users in your organization as necessary for collaboration
      
We do not sell your personal information to third parties.`,
    },
    {
      title: "4. Data Security",
      content: `We implement appropriate technical and organizational measures to protect your personal information, including:
      
• Encryption of data in transit and at rest
• Regular security assessments and audits
• Access controls and authentication mechanisms
• Employee training on data protection
• Incident response procedures
      
However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.`,
    },
    {
      title: "5. Data Retention",
      content: `We retain your personal information for as long as necessary to:
      
• Provide our services to you
• Comply with legal obligations
• Resolve disputes and enforce our agreements
• Maintain business records
      
When information is no longer needed, we will securely delete or anonymize it.`,
    },
    {
      title: "6. Your Rights and Choices",
      content: `You have the following rights regarding your personal information:
      
• Access: Request access to your personal data
• Correction: Request correction of inaccurate or incomplete data
• Deletion: Request deletion of your personal data
• Portability: Request a copy of your data in a portable format
• Objection: Object to certain processing of your data
• Restriction: Request restriction of processing in certain circumstances
      
To exercise these rights, please contact us at privacy@orunal.com`,
    },
    {
      title: "7. Cookies and Tracking Technologies",
      content: `We use cookies and similar tracking technologies to:
      
• Remember your preferences and settings
• Understand how you use our platform
• Improve our services and user experience
• Provide security features
      
You can control cookies through your browser settings, but disabling cookies may affect platform functionality.`,
    },
    {
      title: "8. Children's Privacy",
      content: `Our platform is not intended for children under the age of 16. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.`,
    },
    {
      title: "9. International Data Transfers",
      content: `Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.`,
    },
    {
      title: "10. Changes to This Privacy Policy",
      content: `We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date. Your continued use of our services after changes constitutes acceptance of the updated policy.`,
    },
    {
      title: "11. Contact Us",
      content: `If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
      
Email: privacy@orunal.com
Address: Orunal Technologies Private Limited
      
We aim to respond to all inquiries within 30 days.`,
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        py: 6,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mb: 3,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <ArrowBack
              sx={{ mr: 2, cursor: "pointer", fontSize: 28 }}
              onClick={() => navigate("/")}
            />
            <Security sx={{ mr: 2, fontSize: 36 }} />
            <Typography variant="h3" component="h1" fontWeight="bold">
              Privacy Policy
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ opacity: 0.9 }}>
            Last Updated: February 9, 2026
          </Typography>
          <Divider sx={{ my: 2, backgroundColor: "rgba(255,255,255,0.3)" }} />
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            At Orunal, we are committed to protecting your privacy and ensuring
            the security of your personal information. This Privacy Policy
            explains how we collect, use, disclose, and safeguard your data
            when you use our platform.
          </Typography>
        </Paper>

        {/* Content Sections */}
        {sections.map((section, index) => (
          <Paper
            key={index}
            elevation={2}
            sx={{
              p: 4,
              mb: 3,
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: 6,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
              <Policy sx={{ mr: 2, color: "#667eea", mt: 0.5 }} />
              <Typography
                variant="h5"
                component="h2"
                fontWeight="bold"
                color="primary"
              >
                {section.title}
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                color: "text.secondary",
                whiteSpace: "pre-line",
              }}
            >
              {section.content}
            </Typography>
          </Paper>
        ))}

        {/* Footer Note */}
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mt: 4,
            backgroundColor: "#f5f5f5",
            border: "2px solid #667eea",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Info sx={{ mr: 1, color: "#667eea" }} />
            <Typography variant="h6" fontWeight="bold" color="primary">
              Important Notice
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            By using our platform, you acknowledge that you have read,
            understood, and agree to be bound by this Privacy Policy. If you do
            not agree with any part of this policy, please discontinue use of
            our services immediately.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;

